/**
 * The dialog the PIN gate shows.
 *
 * Deliberately the same shape as `ConfirmDialog` -- §36's requirements are
 * what shape both: focus moves in on open and back to the trigger on close,
 * Escape cancels, the backdrop is inert to a child's stray tap, and the thing
 * is labelled so a screen reader announces what is about to happen rather
 * than reading out two buttons.
 *
 * It is a separate component rather than a prop on ConfirmDialog because a
 * wrong PIN has to be correctable in place. Closing and reopening for a typo
 * is how a four-digit code becomes six taps, and the attempt counter the
 * backend returns is the difference between "wrong again" and knowing the
 * account is one try from locking.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';

import { toApiError, type ApiError } from '@/api/errors';
import { Button } from '@/components/ui/Button';
import { PinInput } from '@/components/ui/PinInput';
import { useLanguage } from '@/features/language/LanguageProvider';
import { noPinSet, type PendingPinRequest } from './usePinGate';

export function PinDialog({ request }: { request: PendingPinRequest }) {
  const { t, language } = useLanguage();
  const field = useRef<HTMLInputElement>(null);
  const returnFocusTo = useRef<Element | null>(null);

  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    returnFocusTo.current = document.activeElement;
    field.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') request.cancel();
    }
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (returnFocusTo.current instanceof HTMLElement) returnFocusTo.current.focus();
    };
    // Mount and unmount only: re-running this on every `request` identity
    // would steal focus back mid-typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await request.attempt(pin);
    } catch (cause) {
      // Only a PIN problem comes back here; the gate sends anything else to
      // whoever asked for the action.
      setPin('');
      setError(toApiError(cause));
    } finally {
      setBusy(false);
    }
  }

  // The backend says how many tries are left. Showing it is the difference
  // between "wrong again" and knowing the account is about to lock.
  const attemptsLeft =
    error?.code === 'PIN_INCORRECT' && typeof error.details.attempts_remaining === 'number'
      ? error.details.attempts_remaining
      : null;

  // There is no PIN on this account, so there is nothing to type. The way
  // through is the one that always works: sign in with WhatsApp again.
  const unset = error !== null && noPinSet(error);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-[var(--space-4)] sm:items-center">
      {/* Backdrop only. A stray tap outside the box answers nothing. */}
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" />

      <form
        onSubmit={onSubmit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pin-gate-title"
        className="relative flex w-full max-w-sm flex-col gap-[var(--space-4)] rounded-[var(--radius-xl)] bg-[var(--color-surface)] p-[var(--space-6)] shadow-[var(--shadow-lg)]"
      >
        <h2
          id="pin-gate-title"
          className="text-[length:var(--text-lg)] font-[var(--weight-bold)]"
        >
          {request.title}
        </h2>

        <p className="text-[length:var(--text-base)] text-[var(--color-text-muted)]">
          {unset ? t('pin.noneSet') : (request.body ?? t('pin.body'))}
        </p>

        {unset ? null : (
          <PinInput
            name="pin-gate"
            label={t('pin.label')}
            minLength={4}
            maxLength={6}
            required
            ref={field}
            value={pin}
            onChange={(event) => setPin(event.currentTarget.value)}
            invalid={error !== null}
          />
        )}

        {/* One live region for the error and the countdown together, so a
            screen reader hears "that PIN isn't right, 3 tries left" as one
            message rather than two. */}
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--color-danger)]">
          {error && !unset ? error.displayMessage(language) : ''}
          {attemptsLeft !== null ? ` ${t('parent.unlock.attempts', { count: attemptsLeft })}` : ''}
        </p>

        <div className="flex flex-col gap-[var(--space-2)]">
          {unset ? null : (
            <Button
              type="submit"
              size="lg"
              fullWidth
              disabled={pin.length < 4}
              busy={busy}
              busyLabel={t('common.loading')}
            >
              {t('pin.submit')}
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            size="md"
            fullWidth
            onClick={request.cancel}
            disabled={busy}
          >
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
}
